/**
 * RoofLayoutEditor - PV Installation Layout Designer
 * 
 * Professional component for:
 * - Satellite map view with address search
 * - Roof area definition
 * - Module placement visualization
 * - Tilt, azimuth, and orientation settings
 * - Production estimation via PVGIS
 * - Basic shadow analysis
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import api from '../lib/api';
import {
  MapPin,
  Search,
  Sun,
  Compass,
  RotateCw,
  Square,
  Grid3X3,
  Zap,
  Cloud,
  Calendar,
  TrendingUp,
  Loader2,
  RefreshCw,
  Download,
  Info,
  AlertTriangle,
  CheckCircle2,
  Maximize2,
  ChevronDown
} from 'lucide-react';

// Dynamic import for Leaflet (client-side only)
let MapContainer, TileLayer, Polygon, Marker, useMap;
if (typeof window !== 'undefined') {
  const L = require('leaflet');
  const RL = require('react-leaflet');
  MapContainer = RL.MapContainer;
  TileLayer = RL.TileLayer;
  Polygon = RL.Polygon;
  Marker = RL.Marker;
  useMap = RL.useMap;
  
  // Fix Leaflet default marker icons
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  });
}

// Import Leaflet CSS
import 'leaflet/dist/leaflet.css';

// Address Search Component
const MAP_CONTAINER_STYLE = { height: '100%', width: '100%' };

function AddressSearch({ onLocationSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const searchAddress = async () => {
    if (!query || query.length < 3) return;
    
    setLoading(true);
    try {
      // Use Nominatim for geocoding (free)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&countrycodes=it`
      );
      const data = await response.json();
      setResults(data);
      setShowResults(true);
    } catch (error) {
      console.error('Geocoding error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (result) => {
    onLocationSelect({
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      address: result.display_name
    });
    setQuery(result.display_name.split(',')[0]);
    setShowResults(false);
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchAddress()}
            placeholder="Cerca indirizzo..."
            className="pl-10"
            data-testid="address-search-input"
          />
        </div>
        <Button onClick={searchAddress} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
        </Button>
      </div>
      
      {showResults && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {results.map((result) => (
            <button
              key={result.place_id || result.osm_id || result.display_name}
              onClick={() => handleSelect(result)}
              className="w-full px-4 py-2 text-left hover:bg-zinc-50 flex items-start gap-2"
            >
              <MapPin className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
              <span className="text-sm text-zinc-700">{result.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Map Recenter Component
function MapRecenter({ center }) {
  const map = useMap();
  
  useEffect(() => {
    if (center) {
      map.setView([center.lat, center.lng], 19);
    }
  }, [center, map]);
  
  return null;
}

// Module Layout Visualization
function ModuleLayoutPreview({ layout, moduleSpecs, tilt }) {
  if (!layout || layout.total_modules === 0) {
    return (
      <div className="text-center py-8 text-zinc-400">
        <Grid3X3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
        <p>Configura l'area per visualizzare il layout moduli</p>
      </div>
    );
  }

  const { modules_per_row, num_rows, total_modules } = layout.layout;
  const cellWidth = 100 / modules_per_row;
  const cellHeight = 100 / num_rows;

  return (
    <div className="space-y-4">
      <div className="aspect-square bg-zinc-100 rounded-xl p-4 relative overflow-hidden">
        <div 
          className="w-full h-full grid gap-0.5"
          style={{
            gridTemplateColumns: `repeat(${modules_per_row}, 1fr)`,
            gridTemplateRows: `repeat(${num_rows}, 1fr)`
          }}
        >
          {Array.from({ length: total_modules }).map((_, idx) => (
            <div
              key={`module-${idx}`}
              className="bg-gradient-to-b from-blue-500 to-blue-700 rounded-sm shadow-sm"
              style={{
                transform: `perspective(100px) rotateX(${tilt / 3}deg)`
              }}
            />
          ))}
        </div>
        <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
          {total_modules} moduli
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="bg-zinc-50 rounded-lg p-3">
          <span className="text-zinc-500">Moduli/fila</span>
          <div className="font-mono font-semibold text-zinc-900">{modules_per_row}</div>
        </div>
        <div className="bg-zinc-50 rounded-lg p-3">
          <span className="text-zinc-500">Numero file</span>
          <div className="font-mono font-semibold text-zinc-900">{num_rows}</div>
        </div>
        <div className="bg-zinc-50 rounded-lg p-3">
          <span className="text-zinc-500">Area moduli</span>
          <div className="font-mono font-semibold text-zinc-900">{layout.coverage.module_area_m2} m²</div>
        </div>
        <div className="bg-zinc-50 rounded-lg p-3">
          <span className="text-zinc-500">Copertura</span>
          <div className="font-mono font-semibold text-zinc-900">{(layout.coverage.coverage_ratio * 100).toFixed(0)}%</div>
        </div>
      </div>
    </div>
  );
}

// Production Chart (simplified)
function ProductionChart({ monthlyData }) {
  if (!monthlyData || monthlyData.length === 0) return null;
  
  const maxValue = Math.max(...monthlyData.map(m => m.energy_kwh));
  const months = ['G', 'F', 'M', 'A', 'M', 'G', 'L', 'A', 'S', 'O', 'N', 'D'];
  
  return (
    <div className="h-32 flex items-end justify-between gap-1">
      {monthlyData.map((month, idx) => {
        const height = (month.energy_kwh / maxValue) * 100;
        return (
          <TooltipProvider key={`month-${month.month}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-gradient-to-t from-amber-500 to-amber-300 rounded-t transition-all hover:from-amber-600 hover:to-amber-400"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                  />
                  <span className="text-xs text-zinc-400 mt-1">{months[idx]}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-zinc-900 text-zinc-50 text-xs">
                <p className="font-semibold">{month.energy_kwh.toFixed(0)} kWh</p>
                <p className="text-zinc-400">Mese {month.month}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
}

// Main Component
export default function RoofLayoutEditor({
  moduleSpecs,
  totalModules,
  totalPowerKwp,
  onLayoutChange,
  contractAddress
}) {
  // Location state
  const [location, setLocation] = useState({
    lat: 41.9028,  // Default: Rome
    lng: 12.4964,
    address: contractAddress || ''
  });
  
  // Roof parameters
  const [roofArea, setRoofArea] = useState(50);
  const [tilt, setTilt] = useState(30);
  const [azimuth, setAzimuth] = useState(0);
  const [orientation, setOrientation] = useState('portrait');
  const [rowSpacing, setRowSpacing] = useState(100);
  const [marginMm, setMarginMm] = useState(500);
  
  // Calculation results
  const [layout, setLayout] = useState(null);
  const [production, setProduction] = useState(null);
  const [shadowAnalysis, setShadowAnalysis] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('map');
  const [mapReady, setMapReady] = useState(false);

  // Calculate layout when parameters change
  const calculateLayout = useCallback(async () => {
    if (!moduleSpecs || roofArea <= 0) return;
    
    setLoading(true);
    try {
      const response = await api.post('/pv-design/calculate-roof-layout', {
        roof_area_m2: roofArea,
        module_length_mm: moduleSpecs.length_mm || 2278,
        module_width_mm: moduleSpecs.width_mm || 1134,
        tilt: tilt,
        azimuth: azimuth,
        row_spacing_mm: rowSpacing,
        margin_mm: marginMm,
        orientation: orientation
      });
      setLayout(response.data);
      
      if (onLayoutChange) {
        onLayoutChange(response.data);
      }
    } catch (error) {
      console.error('Layout calculation error:', error);
    } finally {
      setLoading(false);
    }
  }, [moduleSpecs, roofArea, tilt, azimuth, rowSpacing, marginMm, orientation, onLayoutChange]);

  // Calculate production estimate
  const calculateProduction = useCallback(async () => {
    if (!location || totalPowerKwp <= 0) return;
    
    try {
      const response = await api.post('/pv-design/pvgis-estimate', {
        latitude: location.lat,
        longitude: location.lng,
        peak_power_kwp: totalPowerKwp,
        tilt: tilt,
        azimuth: azimuth,
        losses: 14
      });
      setProduction(response.data);
    } catch (error) {
      console.error('Production estimate error:', error);
    }
  }, [location, totalPowerKwp, tilt, azimuth]);

  // Get shadow analysis
  const getShadowAnalysis = useCallback(async () => {
    if (!location) return;
    
    try {
      const response = await api.get('/pv-design/shadow-analysis', {
        params: {
          latitude: location.lat,
          longitude: location.lng,
          tilt: tilt,
          azimuth: azimuth,
          row_spacing_m: rowSpacing / 1000,
          module_height_m: (moduleSpecs?.length_mm || 2278) / 1000
        }
      });
      setShadowAnalysis(response.data);
    } catch (error) {
      console.error('Shadow analysis error:', error);
    }
  }, [location, tilt, azimuth, rowSpacing, moduleSpecs]);

  // Trigger calculations
  useEffect(() => {
    calculateLayout();
  }, [calculateLayout]);

  useEffect(() => {
    if (totalPowerKwp > 0) {
      calculateProduction();
      getShadowAnalysis();
    }
  }, [calculateProduction, getShadowAnalysis, totalPowerKwp]);

  // Handle location change
  const handleLocationSelect = (newLocation) => {
    setLocation(newLocation);
  };

  // Azimuth labels
  const getAzimuthLabel = (az) => {
    if (az === 0) return 'Sud';
    if (az === 90) return 'Ovest';
    if (az === -90) return 'Est';
    if (az === 180 || az === -180) return 'Nord';
    if (az > 0 && az < 90) return 'Sud-Ovest';
    if (az < 0 && az > -90) return 'Sud-Est';
    if (az > 90) return 'Nord-Ovest';
    if (az < -90) return 'Nord-Est';
    return '';
  };

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="roof-layout-editor">
        {/* Location Search */}
        <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
              <MapPin className="h-5 w-5 text-zinc-500" />
              Localizzazione Impianto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <AddressSearch onLocationSelect={handleLocationSelect} />
            
            {location.address && (
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <MapPin className="h-4 w-4" />
                <span>{location.address}</span>
              </div>
            )}

            {/* Map */}
            <div className="h-[300px] rounded-xl overflow-hidden border border-zinc-200">
              {typeof window !== 'undefined' && MapContainer && (
                <MapContainer
                  center={[location.lat, location.lng]}
                  zoom={19}
                  style={MAP_CONTAINER_STYLE}
                  whenReady={() => setMapReady(true)}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                  <MapRecenter center={location} />
                  {location && <Marker position={[location.lat, location.lng]} />}
                </MapContainer>
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-zinc-400">
              <span>Lat: {location.lat.toFixed(4)}° | Lon: {location.lng.toFixed(4)}°</span>
              <span>Satellite View</span>
            </div>
          </CardContent>
        </Card>

        {/* Parameters Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Configuration */}
          <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                <Square className="h-5 w-5 text-zinc-500" />
                Parametri Tetto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Roof Area */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Area disponibile</Label>
                  <span className="font-mono text-sm text-zinc-500">{roofArea} m²</span>
                </div>
                <Slider
                  value={[roofArea]}
                  onValueChange={([v]) => setRoofArea(v)}
                  min={10}
                  max={500}
                  step={5}
                  className="w-full"
                />
              </div>

              {/* Tilt */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <RotateCw className="h-4 w-4 text-zinc-400" />
                    Inclinazione (Tilt)
                  </Label>
                  <span className="font-mono text-sm text-zinc-500">{tilt}°</span>
                </div>
                <Slider
                  value={[tilt]}
                  onValueChange={([v]) => setTilt(v)}
                  min={0}
                  max={60}
                  step={1}
                  className="w-full"
                />
              </div>

              {/* Azimuth */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Compass className="h-4 w-4 text-zinc-400" />
                    Orientamento (Azimut)
                  </Label>
                  <span className="font-mono text-sm text-zinc-500">{azimuth}° ({getAzimuthLabel(azimuth)})</span>
                </div>
                <Slider
                  value={[azimuth]}
                  onValueChange={([v]) => setAzimuth(v)}
                  min={-180}
                  max={180}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-zinc-400">
                  <span>Est (-90°)</span>
                  <span>Sud (0°)</span>
                  <span>Ovest (90°)</span>
                </div>
              </div>

              {/* Orientation */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Orientamento moduli</Label>
                <Select value={orientation} onValueChange={setOrientation}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait (verticale)</SelectItem>
                    <SelectItem value="landscape">Landscape (orizzontale)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Spacing */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Spaziatura file</Label>
                  <span className="font-mono text-sm text-zinc-500">{rowSpacing} mm</span>
                </div>
                <Slider
                  value={[rowSpacing]}
                  onValueChange={([v]) => setRowSpacing(v)}
                  min={50}
                  max={500}
                  step={10}
                  className="w-full"
                />
              </div>
            </CardContent>
          </Card>

          {/* Right: Layout Preview */}
          <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                <Grid3X3 className="h-5 w-5 text-zinc-500" />
                Layout Moduli
                {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ModuleLayoutPreview 
                layout={layout} 
                moduleSpecs={moduleSpecs}
                tilt={tilt}
              />
            </CardContent>
          </Card>
        </div>

        {/* Production Estimation */}
        {production && (
          <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                <Sun className="h-5 w-5 text-amber-500" />
                Stima Producibilità
                {production.source === 'simplified_calculation' && (
                  <Badge variant="outline" className="text-xs">Calcolo semplificato</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Annual Summary */}
                <div className="lg:col-span-1 space-y-4">
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                    <div className="text-sm text-amber-700 mb-1">Produzione annua stimata</div>
                    <div className="text-3xl font-mono font-bold text-amber-900">
                      {production.annual_production.energy_kwh.toLocaleString('it-IT')}
                    </div>
                    <div className="text-sm text-amber-600">kWh/anno</div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-zinc-50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Resa specifica</div>
                      <div className="font-mono font-semibold">
                        {production.annual_production.specific_yield_kwh_kwp} kWh/kWp
                      </div>
                    </div>
                    <div className="bg-zinc-50 rounded-lg p-3">
                      <div className="text-xs text-zinc-500">Irradiazione</div>
                      <div className="font-mono font-semibold">
                        {production.annual_production.irradiation_kwh_m2} kWh/m²
                      </div>
                    </div>
                  </div>

                  {/* Losses */}
                  {production.losses && (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Perdite orientamento</span>
                        <span className="font-mono">{production.losses.orientation_pct || 0}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Perdite sistema</span>
                        <span className="font-mono">{production.losses.system_pct || production.system.losses_pct}%</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Monthly Chart */}
                <div className="lg:col-span-2">
                  <div className="text-sm text-zinc-500 mb-4">Produzione mensile</div>
                  <ProductionChart monthlyData={production.monthly_production} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Shadow Analysis */}
        {shadowAnalysis && (
          <Card className="bg-white border-zinc-200 rounded-2xl shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                <Cloud className="h-5 w-5 text-zinc-500" />
                Analisi Ombreggiamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`p-4 rounded-xl border ${
                  shadowAnalysis.shading_assessment.risk_level === 'basso' 
                    ? 'bg-emerald-50 border-emerald-200' 
                    : shadowAnalysis.shading_assessment.risk_level === 'medio'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-rose-50 border-rose-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {shadowAnalysis.shading_assessment.risk_level === 'basso' ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    ) : shadowAnalysis.shading_assessment.risk_level === 'medio' ? (
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 text-rose-500" />
                    )}
                    <span className="font-semibold capitalize">Rischio {shadowAnalysis.shading_assessment.risk_level}</span>
                  </div>
                  <p className="text-sm">{shadowAnalysis.shading_assessment.message}</p>
                  <p className="text-xs mt-2 opacity-75">
                    Perdite stimate: ~{shadowAnalysis.shading_assessment.estimated_loss_pct}%
                  </p>
                </div>

                <div className="bg-zinc-50 rounded-xl p-4">
                  <div className="text-sm text-zinc-500 mb-2">Spaziatura file</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Attuale</span>
                      <span className="font-mono">{shadowAnalysis.spacing_analysis.current_spacing_m.toFixed(2)} m</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Minima consigliata</span>
                      <span className="font-mono">{shadowAnalysis.spacing_analysis.min_recommended_m} m</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Ottimale</span>
                      <span className="font-mono">{shadowAnalysis.spacing_analysis.optimal_spacing_m} m</span>
                    </div>
                  </div>
                </div>

                <div className="bg-zinc-50 rounded-xl p-4">
                  <div className="text-sm text-zinc-500 mb-2">Altezza solare (21 Dic)</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Mezzogiorno</span>
                      <span className="font-mono">{shadowAnalysis.sun_position.winter_solstice_altitude}°</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Ore 9:00 / 15:00</span>
                      <span className="font-mono">{shadowAnalysis.sun_position.morning_altitude_9am}°</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}
